import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";
import { toast } from "sonner";
import {
    cartCreate,
    cartLinesAdd,
    cartLinesRemove,
    cartLinesUpdate,
    fetchCart,
} from "./shopify";

/**
 * Shopify-backed cart context.
 * Cart lives in Shopify; we just store the cart ID in localStorage.
 * All state derives from the Shopify Cart object.
 */

const CartContext = createContext(null);
const CART_STORAGE_KEY = "desashop_shopify_cart_id_v1";
const DISCOUNT_KEY = "desashop_discount_code";

export function CartProvider({ children }) {
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [discountCode, setDiscountCode] = useState(
        () => localStorage.getItem(DISCOUNT_KEY) || ""
    );

    // Load existing cart on mount
    useEffect(() => {
        const cartId = localStorage.getItem(CART_STORAGE_KEY);
        if (!cartId) return;
        (async () => {
            try {
                const c = await fetchCart(cartId);
                if (c) setCart(c);
                else localStorage.removeItem(CART_STORAGE_KEY);
            } catch (e) {
                console.warn("Cart fetch failed, clearing", e);
                localStorage.removeItem(CART_STORAGE_KEY);
            }
        })();
    }, []);

    useEffect(() => {
        if (discountCode) localStorage.setItem(DISCOUNT_KEY, discountCode);
        else localStorage.removeItem(DISCOUNT_KEY);
    }, [discountCode]);

    async function ensureCart(initialLines = []) {
        let c = cart;
        if (!c) {
            c = await cartCreate(initialLines);
            localStorage.setItem(CART_STORAGE_KEY, c.id);
            setCart(c);
        }
        return c;
    }

    const addItem = useCallback(
        async (product, qty = 1) => {
            if (!product?.variantId) {
                toast.error("Prodotto non disponibile per la vendita");
                return;
            }
            setLoading(true);
            try {
                let c = cart;
                if (!c) {
                    c = await cartCreate([
                        { merchandiseId: product.variantId, quantity: qty },
                    ]);
                    localStorage.setItem(CART_STORAGE_KEY, c.id);
                } else {
                    c = await cartLinesAdd(c.id, [
                        { merchandiseId: product.variantId, quantity: qty },
                    ]);
                }
                setCart(c);
                setOpen(true);
            } catch (e) {
                console.error(e);
                toast.error("Impossibile aggiungere al carrello");
            } finally {
                setLoading(false);
            }
        },
        [cart]
    );

    const removeItem = useCallback(
        async (lineId) => {
            if (!cart) return;
            setLoading(true);
            try {
                const c = await cartLinesRemove(cart.id, [lineId]);
                setCart(c);
            } catch (e) {
                console.error(e);
                toast.error("Errore rimozione");
            } finally {
                setLoading(false);
            }
        },
        [cart]
    );

    const updateQty = useCallback(
        async (lineId, quantity) => {
            if (!cart) return;
            if (quantity < 1) return removeItem(lineId);
            setLoading(true);
            try {
                const c = await cartLinesUpdate(cart.id, [
                    { id: lineId, quantity },
                ]);
                setCart(c);
            } catch (e) {
                console.error(e);
                toast.error("Errore aggiornamento");
            } finally {
                setLoading(false);
            }
        },
        [cart, removeItem]
    );

    function clear() {
        localStorage.removeItem(CART_STORAGE_KEY);
        setCart(null);
        setDiscountCode("");
    }

    /** Normalized items list for UI compatibility */
    const items = useMemo(() => {
        if (!cart) return [];
        return (cart.lines?.edges || []).map((edge) => {
            const l = edge.node;
            const m = l.merchandise;
            return {
                lineId: l.id,
                product_id: m.product?.id,
                variant_id: m.id,
                quantity: l.quantity,
                name: m.product?.title || m.title,
                image: m.image?.url || m.product?.featuredImage?.url,
                price: parseFloat(m.price.amount),
                currency: m.price.currencyCode,
                handle: m.product?.handle,
            };
        });
    }, [cart]);

    const subtotal = useMemo(() => {
        if (!cart?.cost?.subtotalAmount) {
            return items.reduce((s, i) => s + i.price * i.quantity, 0);
        }
        return parseFloat(cart.cost.subtotalAmount.amount);
    }, [cart, items]);

    const count = useMemo(
        () => cart?.totalQuantity || 0,
        [cart]
    );

    const checkoutUrl = cart?.checkoutUrl || null;

    return (
        <CartContext.Provider
            value={{
                cart,
                items,
                loading,
                addItem,
                removeItem,
                updateQty,
                clear,
                open,
                setOpen,
                subtotal,
                count,
                discountCode,
                setDiscountCode,
                checkoutUrl,
                ensureCart,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
